import { ROOT_PARENT } from 'src/components/WidgetMaker/WidgetDnD/CreateHTML';
import { IWidgetDndInitialState } from '../types';
import { WIDGET_DND_ACTION_TYPES } from './actionTypes';
import cloneDeep from 'lodash/cloneDeep';
import { GLOBAL_SETTING_CONFIG } from 'src/components/WidgetMaker/WidgetDnD/GlobalStyle/globalSettingConstant';
import { SECTION_TYPE } from 'src/components/WidgetMaker/WidgetDnD/WidgetProperties/FooterBuilder/constant';
import { GLOBAL_STYLE } from 'src/constants/globalConfigs';
import { REEL_EMPTY_DATA } from 'src/components/WidgetMaker/WidgetDnD/WidgetProperties/FeaturedReels/FeaturedReelsConfig';
import { BUILDER_WIDGET_ROUTES, SUBSCRIPTION_NAMES } from '@/constants/builderConstants';
import { MULTI_TAB_VALUE } from 'src/components/WidgetMaker/utils/constant';

const DEFAULT_WIDGET = {
  'page-builder': {
    id: 'page-builder',
    name: 'page-builder',
    parent: '',
    children: [],
    props: {},
  },
};

export const REDIRECTION_SOURCE = {
  WIDGET_LIST: 'widgetLIst',
  ACTIVE_WIDGET: 'activeWidget',
};

const DEFAULT_HEADER_DATA = {
  headerName: null,
  headerComponent: null,
  headerPropsConfig: {},
};

const DEFAULT_FOOTER_DATA = {
  footerName: null,
  footerComponent: null,
  footerPropsConfig: {},
};

export const WIDGET_DND_INITIAL_STATE: IWidgetDndInitialState = {
  widgets: DEFAULT_WIDGET,
  pageStyle: {},
  widgetsByRoute: {
    [BUILDER_WIDGET_ROUTES.HOME]: {
      jsonPageWidgetSchema: {
        id: 0,
        sub_type: '',
        name: '',
        images: null,
        order: 4,
        widget_id: '',
        status: 1,
        updated_at: '',
      },
      widgets: DEFAULT_WIDGET,
      style: {},
      loadedFromServer: false,
      widgetsUpdated: false,
    },
  },
  currentSelectedRoute: BUILDER_WIDGET_ROUTES.HOME,
  mySubscription: SUBSCRIPTION_NAMES.ELITE,
  activeWidgetId: null,
  hasUnsavedChanges: false,
  sidePanelState: {
    showWidgetOutline: true,
    showAddWidgetDrawerSection: false,
    showActiveWidgetProperty: false,
    showStyleLayout: false,
    showStyleByRouteOutline: false,
    config: { leftPanelBuilderConfigID: null },
  },
  indexToAddWidgetAtFromMainSection: null,
  activeHeaderData: DEFAULT_HEADER_DATA,
  activeFooterData: DEFAULT_FOOTER_DATA,
  isHeaderFooterPropertyActive: {
    status: false,
    type: null,
  },
  widgetDeleteModalOpen: false,
  widgetIdToDelete: null,
  showPreview: true,
  previewDevice: 'desktop',
  openPostPublishModal: false,
  activeItem: {
    outline: true,
    styleTemplate: false,
  },
  widgetSettingsSectionState: {
    singleBannerSettings: {
      show: false,
      data: {},
      heading: '',
    },
    subWidgetSetting: {
      src: REDIRECTION_SOURCE.WIDGET_LIST,
    },
  },
  globalSettings: GLOBAL_SETTING_CONFIG,
  globalStyle: GLOBAL_STYLE,
  footerChild: {
    type: SECTION_TYPE.FOOTER_SELECTION,
    layer: '',
    index: 0,
    displayText: 'Footer',
  },
  isBuilder: false,
  customTags: [],
  variantData: [],
  snackbarConfig: {
    open: false,
    message: '',
  },
  reelsData: REEL_EMPTY_DATA,
  selectedWidgetSessionState: {
    isAvatarLoading: false,
  },
  allProductTestimonial: [],
  allCategoryProductTestimonial: [],
  fetchingInstaData: false,
  instaUserData: {},
  outlineTabType: MULTI_TAB_VALUE.SECTION,
};

// By default add page-builder via DEFAULT_WIDGET, which can be overriden if it is already present
export function generateWidgetData(widgets = {}) {
  const isParentBuilderPresent = widgets?.[ROOT_PARENT];
  let defaultBuilderParent = DEFAULT_WIDGET;
  if (!isParentBuilderPresent) {
    const newParent = cloneDeep(DEFAULT_WIDGET);
    newParent[ROOT_PARENT].children = Object.entries(widgets).reduce(
      (current: any, acc) => {
        const [widgetID, widgetValue] = current;
        if (widgetID && widgetValue?.parent === ROOT_PARENT) {
          acc.push(widgetID);
        }
        return acc;
      },
      []
    );
    defaultBuilderParent = newParent;
  }
  return { ...defaultBuilderParent, ...widgets };
}

export const WidgetDndContextReducer = (state = WIDGET_DND_INITIAL_STATE, action) => {
  switch (action.type) {
    case WIDGET_DND_ACTION_TYPES.UPDATE_WIDGETS:
      return { ...state, widgets: generateWidgetData(action.payload) };
    case WIDGET_DND_ACTION_TYPES.UPDATE_WIDGETS_BY_ROUTE: {
      const payloadInit = cloneDeep(action.payload.routeConfigs);
      const widgets = generateWidgetData(
        action.payload.routeConfigs.widgets || DEFAULT_WIDGET
      );
      delete payloadInit.widgets;
      const dataToSave = {
        ...payloadInit,
        widgets,
      };
      // This is used to select route config to show on right side of builder
      const shouldSelectRouteConfig = action.payload.shouldSelectRouteConfig !== false;
      const routeName = action.payload.routeName || state.currentSelectedRoute;
      const currentData = state.widgetsByRoute[routeName] || {};
      return {
        ...state,
        ...(shouldSelectRouteConfig
          ? { widgets: widgets, pageStyle: dataToSave.style }
          : {}),
        widgetsByRoute: {
          ...state.widgetsByRoute,
          [routeName]: {
            ...currentData,
            ...dataToSave,
            loadedFromServer: true,
            widgetsUpdated: currentData?.loadedFromServer ? true : false,
          },
        },
      };
    }
    case WIDGET_DND_ACTION_TYPES.SET_ACTIVE_WIDGET:
      return { ...state, activeWidgetId: action.payload };
    case WIDGET_DND_ACTION_TYPES.PAGE_UPDATE_STATUS:
      return { ...state, hasUnsavedChanges: action.payload };
    case WIDGET_DND_ACTION_TYPES.UPDATE_SIDE_PANEL_STATE:
      return { ...state, sidePanelState: { ...state.sidePanelState, ...action.payload } };
    case WIDGET_DND_ACTION_TYPES.ADD_WIDGET_INDEX:
      return { ...state, indexToAddWidgetAtFromMainSection: action.payload };
    case WIDGET_DND_ACTION_TYPES.ADD_WIDGET_INDEX:
      return { ...state, indexToAddWidgetAtFromMainSection: action.payload };
    case WIDGET_DND_ACTION_TYPES.SET_ACTIVE_HEADER_DATA:
      return { ...state, hasUnsavedChanges: true, activeHeaderData: action.payload };
    case WIDGET_DND_ACTION_TYPES.SET_ACTIVE_FOOTER_DATA:
      return { ...state, hasUnsavedChanges: true, activeFooterData: action.payload };
    case WIDGET_DND_ACTION_TYPES.SET_HEADER_FOOTER_PROPERTY_ACTIVE_STATUS:
      return {
        ...state,
        isHeaderFooterPropertyActive: {
          ...state.isHeaderFooterPropertyActive,
          ...action.payload,
        },
      };
    case WIDGET_DND_ACTION_TYPES.SET_WIDGET_DELETE_MODAL_STATUS:
      return { ...state, widgetDeleteModalOpen: action.payload };
    case WIDGET_DND_ACTION_TYPES.SET_WIDGET_ID_TO_DELETE:
      return { ...state, widgetIdToDelete: action.payload };
    case WIDGET_DND_ACTION_TYPES.SET_SHOW_PREVIEW:
      return { ...state, showPreview: action.payload };
    case WIDGET_DND_ACTION_TYPES.TOGGLE_PREVIEW_DEVICE:
      return {
        ...state,
        previewDevice:
          action.payload || (state.previewDevice === 'desktop' ? 'mobile' : 'desktop'),
      };
    case WIDGET_DND_ACTION_TYPES.SET_POST_PUBLISH_MODAL_STATUS:
      return { ...state, openPostPublishModal: action.payload };
    case WIDGET_DND_ACTION_TYPES.SET_ACTIVE_ITEM:
      return { ...state, activeItem: { ...state.activeItem, ...action.payload } };
    case WIDGET_DND_ACTION_TYPES.SET_WIDGET_SETTINGS_SECTION_STATE:
      return {
        ...state,
        widgetSettingsSectionState: {
          ...state.widgetSettingsSectionState,
          ...action.payload,
        },
      };
    case WIDGET_DND_ACTION_TYPES.SET_GLOBAL_SETTING:
      return {
        ...state,
        hasUnsavedChanges: true,
        globalSettings: {
          ...state.globalSettings,
          ...action.payload,
        },
      };
    case WIDGET_DND_ACTION_TYPES.SET_GLOBAL_STYLE:
      return {
        ...state,
        hasUnsavedChanges: true,
        globalStyle: {
          ...state.globalStyle,
          ...action.payload,
        },
      };
    case WIDGET_DND_ACTION_TYPES.FOOTER_WIDGET_SECTION:
      return {
        ...state,
        footerChild: {
          ...state.footerChild,
          ...action.payload,
        },
      };
    case WIDGET_DND_ACTION_TYPES.SET_BUILDER_FLAG:
      return {
        ...state,
        isBuilder: action.payload,
      };
    case WIDGET_DND_ACTION_TYPES.SET_CUSTOM_TAG:
      return {
        ...state,
        customTags: action.payload,
      };
    case WIDGET_DND_ACTION_TYPES.SET_SNACKBAR_CONFIG:
      return {
        ...state,
        snackbarConfig: { ...state.snackbarConfig, ...action.payload },
      };
    case WIDGET_DND_ACTION_TYPES.SET_VARIANT_DATA:
      return {
        ...state,
        variantData: action.payload,
      };
    case WIDGET_DND_ACTION_TYPES.SET_REELS_OUTLINE_DATA:
      return {
        ...state,
        reelsData: {
          ...state.reelsData,
          ...action.payload,
        },
      };
    case WIDGET_DND_ACTION_TYPES.SET_MY_SUBSCRIPTION_TYPE:
      return {
        ...state,
        mySubscription: action.payload,
      };
    case WIDGET_DND_ACTION_TYPES.SET_SELECTED_WIDGET_SESSION_STATE:
      return {
        ...state,
        selectedWidgetSessionState: {
          ...state.selectedWidgetSessionState,
          ...action.payload,
        },
      };
    case WIDGET_DND_ACTION_TYPES.IS_INSTA_DATA_FETCHING:
      return {
        ...state,
        fetchingInstaData: action.payload,
      };
    case WIDGET_DND_ACTION_TYPES.INSTA_USER_DATA:
      return {
        ...state,
        instaUserData: action.payload,
      };
    case WIDGET_DND_ACTION_TYPES.UPDATE_SELECTED_ROUTE_TYPE:
      return {
        ...state,
        currentSelectedRoute: action.payload,
      };
    case WIDGET_DND_ACTION_TYPES.USE_PREEXISTING_ROUTE_CONFIGS: {
      const selectedRoute = action.payload;
      return {
        ...state,
        currentSelectedRoute: selectedRoute,
        widgets: state.widgetsByRoute[selectedRoute].widgets,
        pageStyle: state.widgetsByRoute[selectedRoute].style,
      };
    }
    case WIDGET_DND_ACTION_TYPES.SET_ALL_PRODUCT_TESTIMONIAL_DATA:
      return {
        ...state,
        allProductTestimonial: action.payload,
      };
    case WIDGET_DND_ACTION_TYPES.SET_OUTLINE_TAB_TYPE:
      return {
        ...state,
        outlineTabType: action.payload,
      };
    default:
      return state;
  }
};
